import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, EmptyState, PageLoader, Badge, Money, formatDate, formatWindow } from "@/components/dash";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CalendarClock, ShoppingBag, Sparkles, ClipboardList, Clock, CheckCircle2, Circle, Rocket, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

export default function Overview() {
  const { bakery } = useAuth();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/overview"), api.get("/setup-progress")])
      .then(([o, p]) => { setData(o.data); setProgress(p.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  const nb = data?.next_bake_day;
  const setupSteps = progress ? [
    ["add_product", "Add a product", "/dashboard/products"],
    ["create_bake_day", "Create a bake day", "/dashboard/bake-days"],
    ["add_pickup_windows", "Add pickup windows", "/dashboard/bake-days"],
    ["publish", "Publish your storefront", "/dashboard/storefront"],
    ["share", "Share your ordering link", "/dashboard/storefront"],
  ] : [];
  const done = setupSteps.filter(([k]) => progress[k]).length;
  const setupComplete = done === setupSteps.length;

  return (
    <div className="space-y-6">
      {!setupComplete && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-serif text-xl font-semibold text-cocoa">Finish setting up</h2>
              <p className="text-sm text-cocoa/60">{done} of {setupSteps.length} steps complete</p>
            </div>
            <Rocket strokeWidth={1.5} className="h-6 w-6 text-terracotta" />
          </div>
          <Progress value={(done / setupSteps.length) * 100} className="mb-4 h-2 bg-cocoa/10" />
          <div className="grid gap-2 sm:grid-cols-2">
            {setupSteps.map(([key, label, to]) => (
              <Link key={key} to={to} className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${progress[key] ? "border-sage/40 bg-sage/10 text-cocoa/70" : "border-cocoa/10 bg-cream hover:border-terracotta/40"}`} data-testid={`setup-${key}`}>
                {progress[key] ? <CheckCircle2 className="h-5 w-5 text-sage" /> : <Circle className="h-5 w-5 text-cocoa/30" />}
                <span className={progress[key] ? "line-through" : "font-medium text-cocoa"}>{label}</span>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {nb ? (
        <>
          <Card>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-terracotta">Next bake day</p>
                <h2 className="font-serif text-2xl text-cocoa">{nb.title}</h2>
                <p className="text-sm text-cocoa/60">{formatDate(nb.pickup_date)}</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <OpenBadge status={nb.status} />
                <VisBadge v={nb.visibility} />
              </div>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cocoa/50">Product inventory</p>
                <div className="space-y-2">
                  {nb.inventory.map((p, i) => {
                    const sold = p.max_quantity - p.remaining;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-cocoa">{p.name}</span>
                          <span className={p.remaining <= 0 ? "font-medium text-terracotta" : "text-cocoa/50"}>{p.remaining <= 0 ? "Sold out" : `${sold}/${p.max_quantity} sold`}</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-cocoa/10">
                          <div className="h-full rounded-full bg-terracotta" style={{ width: `${Math.min(100, (sold / p.max_quantity) * 100)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cocoa/50">Upcoming pickup windows</p>
                <div className="space-y-2">
                  {nb.pickup_windows.map((w) => (
                    <div key={w.id} className="flex items-center justify-between rounded-xl bg-cream px-4 py-2.5 text-sm">
                      <span className="text-cocoa">{formatWindow(w)}</span>
                      <span className="text-cocoa/50">{w.max_orders != null ? `${w.max_orders - w.remaining_slots}/${w.max_orders} booked` : "Open"}</span>
                    </div>
                  ))}
                </div>
                <Link to={`/dashboard/bake-days`}><Button variant="outline" className="mt-3 w-full rounded-full border-cocoa/20 text-cocoa" data-testid="view-production-btn">View production plan</Button></Link>
              </div>
            </div>
          </Card>

          <p className="text-sm text-cocoa/55" data-testid="overview-scope-note">The totals and orders below are for this bake day only.</p>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="overview-stats">
            <Stat icon={CalendarClock} label="Orders" value={nb.order_count} />
            <Stat icon={ShoppingBag} label="Items sold" value={nb.items_sold} />
            <Stat icon={Sparkles} label="Est. gross total" value={<Money value={nb.gross_total} />} />
            <Stat icon={Clock} label="Pickup windows" value={nb.pickup_windows.length} />
          </div>
        </>
      ) : (
        <EmptyState
          icon={CalendarClock}
          title="No upcoming bake days yet"
          body="Create your first bake day with pickup windows and product limits, then open it for orders to see your dashboard come alive."
          action={<Link to="/dashboard/bake-days"><Button className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="create-first-bakeday">Create a bake day</Button></Link>}
        />
      )}

      <Card>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold text-cocoa">Orders for this Bake Day</h2>
          <Link to="/dashboard/orders" className="text-sm font-medium text-terracotta hover:underline">View all</Link>
        </div>
        {data?.recent_orders?.length ? (
          <div className="divide-y divide-cocoa/5">
            {data.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-cocoa">#{o.order_number} · {o.customer_name}</p>
                  <p className="text-xs text-cocoa/50">{o.items.reduce((s, i) => s + i.quantity, 0)} item{o.items.reduce((s, i) => s + i.quantity, 0) !== 1 ? "s" : ""} · <Money value={o.subtotal} /></p>
                  {o.pickup_date && <p className="mt-0.5 flex items-center gap-1 text-xs text-cocoa/40"><CalendarClock className="h-3 w-3" /> Pickup {formatDate(o.pickup_date)}{o.pickup_window ? ` · ${o.pickup_window}` : ""}</p>}
                </div>
                <Badge status={o.status} />
              </div>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-cocoa/50">Orders will appear here once customers start placing them.</p>
        )}
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="p-4">
      <Icon strokeWidth={1.5} className="mb-2 h-4 w-4 text-terracotta" />
      <p className="font-serif text-2xl leading-none text-cocoa sm:text-3xl">{value}</p>
      <p className="mt-1 text-xs text-cocoa/50">{label}</p>
    </Card>
  );
}

function OpenBadge({ status }) {
  if (status === "open") return <span className="rounded-full bg-green-500 px-3 py-1 text-xs font-semibold text-green-950" data-testid="open-badge">Open for ordering</span>;
  return <Badge status={status} />;
}

function VisBadge({ v }) {
  const m = { public: ["Public", "bg-sage/20 text-sage"], link_only: ["Link only", "bg-amber-500/20 text-amber-700"], private: ["Private", "bg-cocoa/10 text-cocoa/60"] };
  const [lbl, cls] = m[v || "public"] || m.public;
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${cls}`} data-testid="visibility-badge">{lbl}</span>;
}
